// src/pages/battle/AddBattlePage.jsx
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import BackButton from "../../components/common/BackButton";


import BattlePageHeader from "../../components/battle/BattlePageHeader";
import ModeSection from "../../components/battle/ModeSection";
import TeamSizeSelect from "../../components/battle/TeamSizeSelect";
import CourseSearchSection from "../../components/battle/CourseSearchSection";
import BattleInfoSection from "../../components/battle/BattleInfoSection";
import SubmitSection from "../../components/battle/SubmitSection";
import { useAddBattle } from "../../hooks/useAddBattle";
import { useJWT } from "../../contexts/JWTContext";

// URL 이동은 하지 않고, 부모에서 넘겨주는 onBack / onSubmitSuccess 콜백만 호출
export default function AddBattlePage({ onBack, onSubmitSuccess }) {
  const { submitting, submitBattle } = useAddBattle();
  const navigate = useNavigate();

  // ✅ 전역 JWT 상태 (우리 공통 규칙)
  const jwt = useJWT();
  const loginUserId = jwt?.userId || null;
  const token = jwt?.token || null;

  // ✅ 이 페이지 전용: 로그인 유저의 성별 타입 (0 혼성 / 1 여 / 2 남)
  const [loginGenderType, setLoginGenderType] = useState(null);
  const isFemaleUser = loginGenderType === 1;
  const isMaleUser = loginGenderType === 2;

  // 기본 상태값들
  const [mode, setMode] = useState("SOLO"); // "SOLO" | "TEAM"
  const [teamSize, setTeamSize] = useState("");
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [selectedCourseName, setSelectedCourseName] = useState("");
  const [battleName, setBattleName] = useState("");
  const [battleDateTime, setBattleDateTime] = useState("");

  // ⭐ 대결에서 사용할 성별 타입 : 0 혼성, 1 여자, 2 남자 (BATTLE.GENDER_TYPE 룰 그대로)
  const [genderType, setGenderType] = useState(0);

  // 코스 선택 콜백 (CourseSearchSection → 여기로)
  const handleCourseSelect = (course) => {
    setSelectedCourseId(course.courseId);
    setSelectedCourseName(course.courseName);
  };

  // ==============================
  //  로그인 유저 성별 조회 (/user/{userId})
  // ==============================
  useEffect(() => {
    if (!loginUserId || loginGenderType !== null) {
      return;
    }

    const fetchUserGender = async () => {
      try {
        const res = await fetch(`/user/${encodeURIComponent(loginUserId)}`, {
          headers: token
            ? {
                Authorization: `Bearer ${token}`,
              }
            : undefined,
        });

        if (!res.ok) {
          console.error("유저 조회 실패:", res.status);
          return;
        }

        const data = await res.json();
        const g = data.gender; // '0'(남) / '1'(여) / null

        let gtype = 0; // 기본: 혼성
        if (g === "1" || g === 1) {
          gtype = 1; // 여자
        } else if (g === "0" || g === 0) {
          gtype = 2; // 남자
        }

        setLoginGenderType(gtype);
      } catch (e) {
        console.error("유저 성별 조회 중 오류:", e);
      }
    };

    fetchUserGender();
  }, [loginUserId, token, loginGenderType]);

  // ==============================
  //  잘못된 성별 선택시 자동 보정
  //  (여자인데 남자전 / 남자인데 여자전 선택하면 혼성으로 돌리기)
  // ==============================
  useEffect(() => {
    if (isFemaleUser && genderType === 2) {
      setGenderType(0);
    }
    if (isMaleUser && genderType === 1) {
      setGenderType(0);
    }
  }, [isFemaleUser, isMaleUser, genderType]);

  // ==============================
  //  대결 생성 처리
  // ==============================
  const handleSubmit = async () => {
    // 0) 로그인 체크
    if (!loginUserId) {
      alert("로그인이 필요합니다.");
      return;
    }

    // 1) 성별 조합 검증 (여자는 남자전 불가, 남자는 여자전 불가)
    if (loginGenderType != null) {
      // 여자인데 남자전(2) 선택
      if (loginGenderType === 1 && genderType === 2) {
        alert("여자 생성자는 혼성 / 여자전만 생성할 수 있습니다.");
        return;
      }
      // 남자인데 여자전(1) 선택
      if (loginGenderType === 2 && genderType === 1) {
        alert("남자 생성자는 혼성 / 남자전만 생성할 수 있습니다.");
        return;
      }
    }

    // 2) 코스 선택 체크
    if (!selectedCourseId) {
      alert("코스를 먼저 선택해 주세요.");
      return;
    }

    // 3) 대결명 체크
    if (!battleName.trim()) {
      alert("대결명을 입력해 주세요.");
      return;
    }

    // 4) 날짜/시간 검증 (과거 시간 금지)
    if (battleDateTime) {
      const selected = new Date(battleDateTime);
      const now = new Date();

      if (selected < now) {
        alert("시작 시간은 현재 시각 이후로만 설정할 수 있습니다.");
        return;
      }
    }

    // 5) MODE: 0 = 개인전, 1 = 팀전
    const modeCode = mode === "TEAM" ? 1 : 0;

    // 6) TEAM_SIZE 계산 + 검증
    let teamSizeValue = null; // ✅ 개인전 기본: null

    if (mode === "TEAM") {
      const n = Number(teamSize || 0);

      // 팀전인데 2~10 사이 정수가 아니면 막기
      if (!Number.isInteger(n) || n < 2 || n > 10) {
        alert("팀전은 팀당 인원을 2~10명 사이의 정수로 입력해 주세요.");
        return;
      }

      teamSizeValue = n; // 팀전일 때만 2~10 세팅
    }

    const payload = {
      courseId: selectedCourseId,
      userId: loginUserId, // ✅ JWT에서 가져온 userId
      battleName,
      mode: modeCode, // 0: 개인전, 1: 팀전
      teamSize: teamSizeValue, // ✅ 개인전: null / 팀전: 2~10
      startDatetime: battleDateTime || null,
      status: null, // ServiceImpl에서 0(모집중)
      genderType: genderType, // 0 혼성 / 1 여 / 2 남
    };

    try {
      const battleId = await submitBattle(payload); // useAddBattle에서 battleId 리턴

      if (!battleId) {
        console.warn("생성은 되었는데 battleId 응답을 못 받음:", battleId);
        alert("대결은 생성됐지만, 상세 화면으로 이동할 ID를 받지 못했습니다.");
        return;
      }

      if (onSubmitSuccess) {
        onSubmitSuccess({ ...payload, battleId });
      } else {
        navigate(`/battle/getBattle?battleId=${battleId}`);
      }
    } catch (e) {
      console.error("대결 생성 실패:", e);
      alert("대결 생성 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.");
    }
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate("/battle/list");
    }
  };

  // ==============================
  //  렌더링
  // ==============================

  return (
    <div className="ws-page ws-page-battle-add min-h-screen flex flex-col bg-gray-50">
    <BattlePageHeader
  title="대결생성"
  showBack={true}
  backButtonProps={{ onClick: handleBack }}
/>


      <main className="ws-main ws-main-battle-add flex-1 p-4 space-y-2">
        {/* 1. 모드 + 팀 인원 + 성별 */}
     <section className="ws-section ws-section-battle-mode space-y-3 bg-white rounded-2xl shadow-sm border border-slate-100 p-4">
          {/* 모드 선택 (개인전/팀전) */}
          <ModeSection value={mode} onChange={setMode} />

          {/* 팀 인원 설정 */}
          <TeamSizeSelect
            value={teamSize}
            onChange={setTeamSize}
            disabled={mode !== "TEAM"}
          />

          {/* ⭐ 성별 선택 : 0 혼성 / 1 여자 / 2 남자
              → 로그인 유저 성별에 따라 라디오에서부터 막기 */}
          <div className="flex gap-4 text-sm mt-2">
            <span className="text-xs text-gray-600">성별</span>

            {/* 혼성: 항상 선택 가능 */}
            <label className="flex items-center gap-1">
              <input
                type="radio"
                name="genderType"
                value={0}
                checked={genderType === 0}
                onChange={() => setGenderType(0)}
              />
              혼성
            </label>

            {/* 여자전: 남자 유저면 선택 불가 */}
            <label className="flex items-center gap-1">
              <input
                type="radio"
                name="genderType"
                value={1}
                checked={genderType === 1}
                onChange={() => setGenderType(1)}
                disabled={isMaleUser}
              />
              여자
            </label>

            {/* 남자전: 여자 유저면 선택 불가 */}
            <label className="flex items-center gap-1">
              <input
                type="radio"
                name="genderType"
                value={2}
                checked={genderType === 2}
                onChange={() => setGenderType(2)}
                disabled={isFemaleUser}
              />
              남자
            </label>
          </div>
        </section>

        {/* 2. 코스 검색 섹션 (검색 + 결과 + 선택 모두 내부에서 처리) */}
        <section className="ws-section ws-section-battle-course bg-white rounded-2xl shadow-sm border border-slate-100 p-4 space-y-3">
         <CourseSearchSection
          selectedCourseId={selectedCourseId}
          selectedCourseName={selectedCourseName}
          onCourseSelect={handleCourseSelect}
        />
         </section>

        {/* 3. 대결명 + 일시 */}
  <section className="ws-section ws-section-battle-info bg-white rounded-2xl shadow-sm border border-slate-100 p-3">

          <BattleInfoSection
            battleName={battleName}
            onBattleNameChange={setBattleName}
            battleDateTime={battleDateTime}
            onBattleDateChange={setBattleDateTime}
          />
        </section>

        {/* 4. 생성 버튼 */}
        <section className="ws-section ws-section-battle-submit">
          <SubmitSection onSubmit={handleSubmit} disabled={submitting} />
        </section>
      </main>
    </div>
  );
}
